import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Button, Card, Col, DatePicker, Input, Row, Select,
  Space, Table, Tag, Typography, message,
} from "antd";
import {
  CalendarOutlined, CopyOutlined, FileExcelOutlined, FilePdfOutlined,
  FilterOutlined, ReloadOutlined, SearchOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import "../css/sm-art-common.css";
import { BASE_URL } from "../services/BaseUrl";

const { Title, Text } = Typography;
const PAGE_SIZE = 10;
const COLUMNS = [
  ["Unit", "unit_name"],
  ["Capacity In Pcs", "capacity_qty"],
  ["Entered On", "entered_on"],
  ["Status", "entered"],
];
const formatNumber = (value) =>
  value === null || value === undefined || value === ""
    ? "—"
    : Number(value).toLocaleString("en-IN");
const formatDateTime = (value) => {
  if (!value) return "—";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD-MM-YYYY hh:mm A") : String(value);
};
const formatUnitLabel = (value) => {
  if (value == null || String(value).trim() === "") return "—";
  const text = String(value).trim().toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
};
const statusLabel = (entered) => entered ? "Entered" : "Pending";
const exportValue = (row, key) => {
  if (key === "entered") return statusLabel(row.entered);
  if (key === "capacity_qty") return row.capacity_qty ?? "";
  if (key === "entered_on" || key === "updated_on") return formatDateTime(row[key]) === "—" ? "" : formatDateTime(row[key]);
  return key === "unit_name" ? formatUnitLabel(row[key]) : (row[key] ?? "");
};
const buildUrl = (date) => {
  const url = new URL("/index.php/backlog/capacity_status", BASE_URL.BASE);
  url.searchParams.set("date", date);
  return url.toString();
};

export default function CapacityStatusReport() {
  const [date, setDate] = useState(() => dayjs().format("YYYY-MM-DD"));
  const [dataRows, setDataRows] = useState([]);
  const [summary, setSummary] = useState({ entered_count: 0, pending_count: 0, total_capacity: 0 });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const requestId = useRef(0);
  const abortRef = useRef(null);

  const load = useCallback(async (reportDate) => {
    const id = ++requestId.current;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError("");
    try {
      const response = await fetch(buildUrl(reportDate), {
        method: "GET",
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`Capacity Status API returned HTTP ${response.status}`);
      const result = await response.json();
      if (result?.status !== true || !Array.isArray(result?.data?.units)) {
        throw new Error(result?.message || "Invalid capacity status response: data.units is missing");
      }
      if (controller.signal.aborted || id !== requestId.current) return;
      const units = result.data.units;
      setDataRows(units);
      setSummary({
        entered_count: Number(result.data.entered_count ?? units.filter((row) => row.entered).length),
        pending_count: Number(result.data.pending_count ?? units.filter((row) => !row.entered).length),
        total_capacity: Number(result.data.total_capacity ?? units.reduce((sum, row) => sum + (Number(row.capacity_qty) || 0), 0)),
      });
      setPage(1);
    } catch (err) {
      if (controller.signal.aborted || id !== requestId.current) return;
      setDataRows([]);
      setSummary({ entered_count: 0, pending_count: 0, total_capacity: 0 });
      setError(err?.message || "Unable to load capacity status report");
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(date);
    return () => {
      ++requestId.current;
      abortRef.current?.abort();
    };
  }, [date, load]);

  const rows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return dataRows.filter((row) => {
      const matchesText = !query || `${row.unit_name ?? ""} ${row.unit_code ?? ""}`.toLowerCase().includes(query);
      const matchesStatus = statusFilter === "all" ||
        (statusFilter === "entered" ? Boolean(row.entered) : !row.entered);
      return matchesText && matchesStatus;
    });
  }, [dataRows, search, statusFilter]);

  const filteredCapacity = useMemo(
    () => rows.reduce((sum, row) => sum + (Number(row.capacity_qty) || 0), 0),
    [rows]
  );

  const exportData = () => [
    ["Sl.No.", ...COLUMNS.map(([label]) => label)],
    ...rows.map((row, index) => [index + 1, ...COLUMNS.map(([, key]) => exportValue(row, key))]),
    ["Total", "", filteredCapacity, "", ""],
  ];

  const onCopy = async () => {
    if (!rows.length) return message.warning("No data to copy");
    try {
      await navigator.clipboard.writeText(exportData().map((record) => record.join("\t")).join("\n"));
      message.success("Report copied");
    } catch {
      message.error("Unable to copy report");
    }
  };

  const onExcel = async () => {
    if (!rows.length) return message.warning("No data to export");
    try {
      const XLSX = await import("xlsx");
      const sheet = XLSX.utils.aoa_to_sheet([
        ["Capacity Status Report"],
        [`Report Date: ${date}`],
        [`Entered: ${summary.entered_count} | Pending: ${summary.pending_count} | Total Capacity: ${summary.total_capacity}`],
        [],
        ...exportData(),
      ]);
      sheet["!cols"] = [{ wch: 19 }, { wch: 19 }, { wch: 18 }, { wch: 24 }, { wch: 14 }];
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, sheet, "Capacity Status");
      XLSX.writeFile(workbook, `Capacity_Status_Report_${date}.xlsx`);
    } catch (err) {
      console.error(err);
      message.error("Excel export failed");
    }
  };

  const onPDF = async () => {
    if (!rows.length) return message.warning("No data to export");
    try {
      const { jsPDF } = await import("jspdf");
      const tableModule = await import("jspdf-autotable");
      const autoTable = tableModule.default || tableModule.autoTable;
      const pdf = new jsPDF("landscape");
      pdf.setFontSize(16);
      pdf.text("Capacity Status Report", 14, 17);
      pdf.setFontSize(10);
      pdf.text(`Report Date: ${date}`, 14, 25);
      pdf.text(`Entered: ${summary.entered_count}  Pending: ${summary.pending_count}  Total Capacity: ${formatNumber(summary.total_capacity)}`, 14, 31);
      const tableData = exportData();
      autoTable(pdf, {
        startY: 38,
        head: [tableData[0]],
        body: tableData.slice(1).map((record) => record.map(String)),
        theme: "grid",
        styles: { fontSize: 9 },
      });
      pdf.save(`Capacity_Status_Report_${date}.pdf`);
    } catch (err) {
      console.error(err);
      message.error("PDF export failed");
    }
  };

  const columns = [
    {
      title: "Sl.No.",
      key: "serial",
      width: 126,
      align: "center",
      render: (_value, _record, index) => (page - 1) * PAGE_SIZE + index + 1,
    },
    {
      title: "Unit",
      dataIndex: "unit_name",
      key: "unit_name",
      width: 125,
      render: (value) => <Text strong>{formatUnitLabel(value)}</Text>,
    },
    {
      title: "Capacity In Pcs",
      dataIndex: "capacity_qty",
      key: "capacity_qty",
      width: 155,
      align: "center",
      render: formatNumber,
    },
    {
      title: "Entered On",
      dataIndex: "entered_on",
      key: "entered_on",
      width: 190,
      render: formatDateTime,
    },
    {
      title: "Status",
      dataIndex: "entered",
      key: "entered",
      width: 130,
      align: "center",
      render: (entered) => (
        <Tag color={entered ? "success" : "warning"} style={{ marginInlineEnd: 0, whiteSpace: "nowrap" }}>
          {statusLabel(entered)}
        </Tag>
      ),
    },
  ];

  const stats = [
    { label: "Total Units", value: dataRows.length },
    { label: "Entered", value: summary.entered_count },
    { label: "Pending", value: summary.pending_count },
    { label: "Total Capacity", value: formatNumber(summary.total_capacity) },
  ];

  return (
    <div className="sm-page capacity-status-page unified-report-page">
      <div className="sm-page-inner">
        <section className="unified-panel">
          <header className="unified-heading">
            <div>
              <Title level={3} className="sm-page-title">Capacity Status Report</Title>
              <Text className="unified-subtitle">Daily capacity entry status · {dayjs(date).format("DD/MM/YYYY")}</Text>
            </div>
            <Text className="unified-records">Total Records <strong>{dataRows.length}</strong></Text>
          </header>
          <div className="unified-toolbar">
            <Input className="unified-search" prefix={<SearchOutlined />} placeholder="Search unit name or code" allowClear
              value={search} onChange={(event) => { setPage(1); setSearch(event.target.value); }} />
            <Select className="unified-status" value={statusFilter} onChange={(v) => { setStatusFilter(v); setPage(1); }}
              options={[{ label: "All Units", value: "all" }, { label: "Entered", value: "entered" }, { label: "Pending", value: "pending" }]} />
            <DatePicker className="unified-date" value={dayjs(date)} allowClear={false} format="DD/MM/YYYY"
              disabledDate={(v) => v && v.isAfter(dayjs(), "day")}
              onChange={(v) => { if (v) { setDate(v.format("YYYY-MM-DD")); setPage(1); } }} />
            <Button icon={<ReloadOutlined />} loading={loading} onClick={() => load(date)}>Refresh</Button>
            <div className="unified-toolbar-actions">
              <Button icon={<CopyOutlined />} disabled={!rows.length} onClick={onCopy}>Copy</Button>
              <Button icon={<FileExcelOutlined />} disabled={!rows.length} onClick={onExcel}>Excel</Button>
              <Button icon={<FilePdfOutlined />} disabled={!rows.length} onClick={onPDF}>PDF</Button>
            </div>
          </div>
          <div className="unified-inline-stats">
            <span>Entered <strong>{summary.entered_count}</strong></span>
            <span>Pending <strong>{summary.pending_count}</strong></span>
            <span>Total Capacity <strong>{formatNumber(summary.total_capacity)}</strong></span>
          </div>
          {error && <Alert className="unified-error" showIcon type="error" message={error} />}
          <div className="unified-table-wrap">
            <Table className="unified-table" rowKey={(record) => String(record.unit_code)} columns={columns}
              dataSource={rows} loading={loading} size="small" scroll={{ x: 850 }}
              pagination={{ current: page, pageSize: PAGE_SIZE, showSizeChanger: false,
                onChange: setPage, showTotal: (total, range) => `${range[0]}–${range[1]} of ${total} units` }}
              summary={() => rows.length ? <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={2}><strong>Total</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right"><strong>{formatNumber(filteredCapacity)}</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={3} />
                <Table.Summary.Cell index={4} align="center">—</Table.Summary.Cell>
              </Table.Summary.Row> : null} />
          </div>
        </section>
      </div>
    </div>
  );
}
